import { useCallback, useEffect, useRef } from 'react'
import homeBgm from '../assets/home.mp3'
import stageBgm from '../assets/stage.mp3'
import {
  getEffectiveBgmVolume,
  subscribeBgmVolume,
} from '../services/volume'
import { isCapacitorNative } from '../utils/platform'

type BgmTrack = 'home' | 'stage' | 'none'

/**
 * 実効音量を HTMLAudioElement に反映する。
 *
 * **`volume` だけでは iOS でミュートできない。** iOS の Safari / WKWebView は
 * `HTMLMediaElement.volume` への代入を無視し、常に 1 として扱う（音量はハード
 * ウェアのボリュームボタンでしか変えられないという Apple の仕様）。そのため
 * 「実効音量 0 = 無音」を保証するには `muted` を併用する必要がある。
 * `muted` は iOS でも設定が効く。
 */
function applyBgmVolume(audio: HTMLAudioElement, effectiveVolume: number): void {
  audio.volume = effectiveVolume
  audio.muted = effectiveVolume === 0
}

/**
 * BGM 再生フック。
 *
 * 音量制御:
 *   - 再生開始時の初期音量は `getEffectiveBgmVolume()` を参照。
 *   - ミュート切替（タイトルの 🔊 / 🔇 と SettingsModal）や広告ダッキングでの変化は
 *     `subscribeBgmVolume()` で受けて `applyBgmVolume()` で即時反映する
 *     （iOS 対策で `volume` と `muted` の両方を設定する。同関数のコメント参照）。
 *   - 実効音量 0 のときも再生自体は継続する（ミュートしても currentTime が進むのみで、
 *     UI 側のトラック切替ロジックと整合を取りやすい）。
 *
 * ライフサイクル制御 (Capacitor ネイティブ):
 *   - `@capacitor/app` の `appStateChange` を購読し、
 *     アプリがバックグラウンド (`isActive=false`) になったら BGM を pause、
 *     フォアグラウンド復帰 (`isActive=true`) で再開する。
 *   - WebView 自体はスリープしないため明示的に audio.pause() しないと
 *     iOS 上でロック画面/別アプリ起動後も BGM が鳴り続ける（実機 18.x 確認済）。
 *   - Web ブラウザは visibilitychange の方が適切だが、本フックは Capacitor 前提。
 */
export function useAudio() {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const currentTrackRef = useRef<BgmTrack>('none')

  // マウント中に実効音量が変わったら、再生中の audio に即時反映する。
  useEffect(() => {
    const unsubscribe = subscribeBgmVolume((nextVolume) => {
      if (audioRef.current) {
        applyBgmVolume(audioRef.current, nextVolume)
      }
    })
    return unsubscribe
  }, [])

  // Capacitor ネイティブで appStateChange を購読し、background で pause / foreground で resume。
  useEffect(() => {
    if (!isCapacitorNative()) return

    let detach: (() => void) | null = null
    let cancelled = false
    let wasPlayingBeforeBackground = false

    void (async () => {
      try {
        const { App } = await import('@capacitor/app')
        if (cancelled) return

        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          const audio = audioRef.current
          if (!audio) return

          if (!isActive) {
            // バックグラウンドへ移行: 現在再生中なら pause し、復帰時に再生していたかを記録する。
            wasPlayingBeforeBackground = !audio.paused
            if (wasPlayingBeforeBackground) {
              audio.pause()
            }
          } else {
            // フォアグラウンドへ復帰: バックグラウンド前に再生していた場合のみ再開。
            if (wasPlayingBeforeBackground) {
              audio.play().catch(() => {
                // fallback:ok — iOS のオーディオセッション競合などで失敗しても致命的ではない。
              })
              wasPlayingBeforeBackground = false
            }
          }
        })

        detach = () => {
          void handle.remove()
        }
      } catch (e) {
        // fallback:ok — @capacitor/app がロードできない環境では lifecycle 制御を諦め、
        // 既存の音量制御だけで動作させる。
        console.warn('[useAudio] App lifecycle listener init failed:', e)
      }
    })()

    return () => {
      cancelled = true
      if (detach) detach()
    }
  }, [])

  const playBgm = useCallback((track: BgmTrack) => {
    if (currentTrackRef.current === track) return

    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.src = ''
    }

    if (track === 'none') {
      currentTrackRef.current = 'none'
      return
    }

    const src = track === 'home' ? homeBgm : stageBgm
    const audio = new Audio(src)
    audio.loop = true
    applyBgmVolume(audio, getEffectiveBgmVolume())
    audio.play().catch(() => {
      // ブラウザ自動再生ポリシーで再生できない場合は無視
      // TODO: ユーザー操作後に再生を試みるリトライ機構を追加
    })
    audioRef.current = audio
    currentTrackRef.current = track
  }, [])

  /**
   * ユーザーが音を ON にした瞬間（タップハンドラ内）に呼ぶ。
   *
   * ミュート中も volume=0 で再生を回し続ける設計だが、起動直後はブラウザの
   * autoplay ポリシーで `play()` が拒否され、要素が paused のまま残ることがある。
   * その場合はユーザージェスチャ内の本呼び出しが実際の再生開始を担う。
   *
   * 実効音量は `subscribeBgmVolume` 経由で反映済みのはずだが、呼び出し順に
   * 依存して「ミュートされたまま再生開始」になるのを防ぐため、ここでも
   * 現在の実効音量を再適用する（`muted` の解除を含む）。
   */
  const resumeBgmIfPaused = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    applyBgmVolume(audio, getEffectiveBgmVolume())
    if (!audio.paused) return
    audio.play().catch((err) => {
      // fallback:ok — ジェスチャ内でも失敗する環境（iOS のオーディオセッション競合等）は
      // 次のトラック切替で再試行される。原因を追えるよう握り潰さずログに残す。
      console.warn('[useAudio] resumeBgmIfPaused failed:', err)
    })
  }, [])

  const stopBgm = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.src = ''
      audioRef.current = null
    }
    currentTrackRef.current = 'none'
  }, [])

  return { playBgm, stopBgm, resumeBgmIfPaused }
}
